import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Container from '@amf/shared/components/layout/Container';
import IconArrowLeft from '@amf/shared/icons/IconArrowLeft';
import { useWindowSize } from '@amf/shared/hooks/useWindowSize';
import SelectField from '@amf/shared/components/form/SelectField';

export type MenuItem<T> = {
  id: string;
  title: string;
  value: T;
};

interface MenuProps<T> {
  items: MenuItem<T>[];
  selected?: MenuItem<T>;
  onChange?: (item: MenuItem<T>) => void;
  children?: ReactNode;
  scrollable?: boolean;
  label?: string;
}

function Menu<T>({ items, children, selected, onChange, scrollable, label }: MenuProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);

  const { width } = useWindowSize();
  const isDesktop = width > 620;

  const change = useCallback(
    (item: MenuItem<T>) => {
      if (!onChange) return;
      onChange(item);
    },
    [onChange],
  );

  const resolve = useCallback(() => {
    if (!ref.current) return;
    if (ref.current.clientWidth === ref.current.scrollWidth) {
      setLeftVisible(false);
      setRightVisible(false);
      return;
    }

    setLeftVisible(ref.current.scrollLeft > 0);
    setRightVisible(
      Math.ceil(ref.current.scrollLeft) + ref.current.clientWidth < ref.current.scrollWidth,
    );
  }, []);

  useEffect(() => {
    resolve();
    setTimeout(() => resolve(), 100);
  }, [resolve]);

  const move = useCallback((direction: 'left' | 'right') => {
    if (!ref.current) return;

    const left =
      direction === 'left'
        ? ref.current.scrollLeft - ref.current.clientWidth
        : ref.current.scrollLeft + ref.current.clientWidth;

    ref.current.scrollTo({ left, behavior: 'smooth' });
  }, []);

  if (!isDesktop && scrollable) {
    return (
      <div className='Menu'>
        <Container>
          <SelectField
            label={label}
            onChange={e => {
              const id = e.target.value;
              const item = items.find(i => i.id === id);
              if (!item) return;
              change(item);
            }}
            value={selected.id}
            style={{ minWidth: 140 }}
            className='Filter__field'
          >
            {items.map(item => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </SelectField>
        </Container>
      </div>
    );
  }

  return (
    <div
      className={classNames('Menu', {
        'Menu--scrollable': scrollable,
      })}
    >
      <Container
        className={classNames('Menu__container', {
          'Menu__container--last': !rightVisible,
        })}
      >
        {leftVisible && (
          <button
            type='button'
            className='Menu__button Menu__button--left'
            onClick={() => move('left')}
          >
            <IconArrowLeft />
          </button>
        )}
        <div ref={ref} className='Menu__wrapper' onScroll={resolve}>
          <div className='Menu__content'>
            {items.map(item => (
              <button
                key={item.id}
                type='button'
                onClick={() => change(item)}
                className={classNames('Menu__item', {
                  'Menu__item--active': selected?.id === item.id,
                })}
              >
                {item.title}
              </button>
            ))}
          </div>
          {children && <div className='Menu__children'>{children}</div>}
        </div>
        {rightVisible && (
          <button
            type='button'
            className='Menu__button Menu__button--right'
            onClick={() => move('right')}
          >
            <IconArrowLeft />
          </button>
        )}
      </Container>
    </div>
  );
}

export default Menu;
